import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Layout } from "../../../../containers/app/layout";
import { PlanDetailsWrapper } from "./styled";
import { getPlanService } from "../../../../utils/apis/plansandpricing/getPlan";
import { getPlanFeatures } from "../../../../utils/apis/plansandpricing/getPlanFeatures";
import { H3, P, Label, Span } from "../../../../components/typography/styled";
import { BaseButton } from "../../../../components/button/styled";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faPencil } from "@fortawesome/free-solid-svg-icons";
import { PlanDetailsEditModal } from "../../../../containers/app/modals/plandetailseditmodal";
import { SuccessModal } from "../../../../containers/app/modals/successmodal";

export const PlanDetails = () => {
  const { planId } = useParams();
  const navigate = useNavigate();

  const [plan, setPlan] = useState(null);
  const [features, setFeatures] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

  useEffect(() => {
    const loadPlanDetails = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const planResponse = await getPlanService(null, planId);
        const planData = planResponse?.data ?? planResponse;
        setPlan(planData);

        const featureResponse = await getPlanFeatures(null, planId);
        setFeatures(featureResponse ?? []);
      } catch (err) {
        console.error("Failed to load plan details:", err);
        setError("Unable to load plan details. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };

    if (planId) {
      loadPlanDetails();
    }
  }, [planId, isSuccessModalOpen]);

  const handleOpenEditModal = () => {
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
  };

  const handleEditSuccess = () => {
    setIsSuccessModalOpen(true);
    setIsEditModalOpen(false);
  };

  const handleCloseSuccessModal = () => {
    setIsSuccessModalOpen(false);
  };

  return (
    <Layout id={"plan-details"} title={"Plan Details"}>
      <PlanDetailsWrapper>
        <SuccessModal
          open={isSuccessModalOpen}
          handleClickOutside={handleCloseSuccessModal}
          className={"update-plan-success-modal"}
          title={"Success"}
          message={"Plan has been successfully updated"}
          callToAction={"Close"}
          handleCallToActionClick={handleCloseSuccessModal}
        />

        <div className="plan-details-header">
          <BaseButton onClick={() => navigate("/admin/pricing")}>
            <div>
              <FontAwesomeIcon icon={faArrowLeft} />
            </div>
            Back to Plans
          </BaseButton>
          <div className="header-center">
            <div>
              <H3 style={{ fontWeight: "bold", margin: 0 }}>PLAN DETAILS</H3>
              <P>Viewing the selected payroll plan and its features.</P>
            </div>
            <BaseButton
              onClick={handleOpenEditModal}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <FontAwesomeIcon icon={faPencil} />
              Edit Plan
            </BaseButton>
          </div>
        </div>

        {isLoading ? (
          <P>Loading plan details…</P>
        ) : error ? (
          <P style={{ color: "red" }}>{error}</P>
        ) : (
          <>
            <div className="plan-summary">
              <div className="plan-summary-header">
                <Label>Plan name</Label>
                <Span>{plan?.title ?? "-"}</Span>
              </div>
              <div>
                <Label>Lower limit</Label>
                <Span>{plan?.lowerLimit ?? "-"}</Span>
              </div>
              <div>
                <Label>Upper limit</Label>
                <Span>{plan?.upperLimit ?? "-"}</Span>
              </div>
              <div>
                <Label>Credit cost per employee</Label>
                <Span>{plan?.creditCostPerEmployee ?? "-"}</Span>
              </div>
            </div>

            <div className="plan-boolean-grid">
              <div>
                <Label>Can run payroll</Label>
                <Span>{plan?.canRunPayroll ? "Yes" : "No"}</Span>
              </div>
              <div>
                <Label>Can remit pension</Label>
                <Span>{plan?.canRemitPension ? "Yes" : "No"}</Span>
              </div>
              <div>
                <Label>Can remit staff on application</Label>
                <Span>{plan?.canRemitStaffOnApplication ? "Yes" : "No"}</Span>
              </div>
            </div>

            <div className="plan-features-table">
              <H3>FEATURES</H3>
              <table>
                <thead>
                  <th>Feature</th>
                  <th>Value</th>
                </thead>
                <tbody>
                  {features?.length > 0 ? (
                    features.map((feature) => (
                      <tr key={feature.id || feature.featureDefinitionId}>
                        <td>{feature.name || feature.code}</td>
                        <td>{feature.value}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={3}>No features found for this plan.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        <PlanDetailsEditModal
          planId={planId}
          isOpen={isEditModalOpen}
          onClose={handleCloseEditModal}
          onSuccess={handleEditSuccess}
          width="70%"
        />
      </PlanDetailsWrapper>
    </Layout>
  );
};
